/**
 * Actionable contact chips — one place to render a phone or email so they
 * always open the OS dialer / mail client and stay stylistically consistent.
 *
 * Deliberately not "blue underlined link" — icon prefix + slate text +
 * subtle hover so it reads like a piece of the layout, not a hyperlink.
 */
import { Mail, MessageSquare, Phone } from "lucide-react";
import { useAuth } from "../lib/auth";

const cls =
  "inline-flex items-center gap-1.5 text-sm text-slate-600 " +
  "hover:text-slate-900 transition-colors group/contact tabular-nums whitespace-nowrap";

const iconCls = "h-3.5 w-3.5 text-slate-400 group-hover/contact:text-slate-700";

// Strips non-digit chars (except leading +) so tel:/sms: work reliably
// across dialers regardless of whether the number was stored as
// "480-555-0187" or "(480) 555-0187".
const digits = (raw) => String(raw).replace(/[^\d+]/g, "");
const telHref = (raw) => `tel:${digits(raw)}`;
const smsHref = (raw, body) => {
  const base = `sms:${digits(raw)}`;
  return body ? `${base}?&body=${encodeURIComponent(body)}` : base;
};

const mailtoHref = (email, subject) =>
  `mailto:${email}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`;

/**
 * <EmailLink email="a@b.com" subjectFrom="Felecia Monroe" />
 * If `subjectFrom` isn't provided the current agent's full name is used.
 * Pass `subject={null}` to opt out of a subject line entirely.
 */
export function EmailLink({ email, subject, subjectFrom, testId, stop = false, className = "" }) {
  const { agent } = useAuth();
  if (!email) return null;
  const from = subjectFrom ?? agent?.full_name;
  const finalSubject =
    subject === null
      ? null
      : subject ?? (from ? `Following up — ${from}` : null);
  return (
    <a
      href={mailtoHref(email, finalSubject)}
      onClick={stop ? (e) => e.stopPropagation() : undefined}
      className={`${cls} ${className}`.trim()}
      data-testid={testId}
    >
      <Mail className={iconCls} />
      <span className="truncate">{email}</span>
    </a>
  );
}

/**
 * Standalone chat-bubble button that opens the OS messaging app. Rendered
 * as a companion next to <PhoneLink> by default (`sms` prop), or usable on
 * its own where a labelled chip isn't needed.
 */
export function SmsLink({ phone, testId, stop = false, className = "", body }) {
  if (!phone) return null;
  return (
    <a
      href={smsHref(phone, body)}
      onClick={stop ? (e) => e.stopPropagation() : undefined}
      className={
        `inline-flex items-center justify-center h-6 w-6 rounded-full ` +
        `text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors ${className}`.trim()
      }
      title={`Text ${phone}`}
      aria-label={`Text ${phone}`}
      data-testid={testId}
    >
      <MessageSquare className="h-3.5 w-3.5" />
    </a>
  );
}

export function PhoneLink({ phone, testId, stop = false, className = "", sms = true, smsBody, smsTestId }) {
  if (!phone) return null;
  const anchor = (
    <a
      href={telHref(phone)}
      onClick={stop ? (e) => e.stopPropagation() : undefined}
      className={`${cls} ${className}`.trim()}
      data-testid={testId}
    >
      <Phone className={iconCls} />
      <span>{phone}</span>
    </a>
  );
  if (!sms) return anchor;
  return (
    <span className="inline-flex items-center gap-1">
      {anchor}
      <SmsLink
        phone={phone}
        stop={stop}
        body={smsBody}
        testId={smsTestId ?? (testId ? `${testId}-sms` : undefined)}
      />
    </span>
  );
}
